import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Check, Loader2, Plus, RefreshCw, Share2 } from 'lucide-react';
import api from '../../../../services/api';
import { biddingTypeLabel } from '../rmfLabels';

export interface PortfolioStrategy {
  resource_name: string;
  id: string;
  name: string;
  type: string;
  status?: string;
  campaigns?: number;
  target_cpa?: number | null;
  target_roas?: number | null;
}

type PortfolioType = 'TARGET_CPA' | 'TARGET_ROAS' | 'MAXIMIZE_CONVERSIONS' | 'MAXIMIZE_CONVERSION_VALUE';

export const usePortfolioTypeLabel = () => {
  const { t } = useTranslation();
  return (type: string) => {
    switch (type) {
      case 'TARGET_CPA': return t('campaigns.rmf.portfolioTypeTargetCpa', 'CPA alvo');
      case 'TARGET_ROAS': return t('campaigns.rmf.portfolioTypeTargetRoas', 'ROAS alvo');
      case 'MAXIMIZE_CONVERSIONS': return t('campaigns.rmf.portfolioTypeMaxConv', 'Maximizar conversões');
      case 'MAXIMIZE_CONVERSION_VALUE': return t('campaigns.rmf.portfolioTypeMaxValue', 'Maximizar valor da conversão');
      default: return biddingTypeLabel(t, type);
    }
  };
};

interface Props {
  value?: string;
  currency?: string;
  onChange: (resourceName: string, name?: string) => void;
}

const GooglePortfolioStrategyPicker: React.FC<Props> = ({ value, currency = 'BRL', onChange }) => {
  const { t } = useTranslation();
  const typeLabel = usePortfolioTypeLabel();
  const [list, setList] = useState<PortfolioStrategy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<PortfolioType>('TARGET_CPA');
  const [newTarget, setNewTarget] = useState('');

  const load = useCallback(async (): Promise<PortfolioStrategy[]> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/campaigns/google/portfolio-strategies');
      const data: PortfolioStrategy[] = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
      setList(data);
      return data;
    } catch (e: any) {
      setError(e?.response?.data?.message || t('campaigns.rmf.portfolioLoadError', 'Não foi possível carregar as estratégias de portfólio do Google Ads.'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const currencySymbol = currency === 'BRL' ? 'R$' : currency;
  const needsCpa = newType === 'TARGET_CPA';
  const needsRoas = newType === 'TARGET_ROAS';
  const optionalRoas = newType === 'MAXIMIZE_CONVERSION_VALUE';

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error(t('campaigns.rmf.portfolioNameRequired', 'Dê um nome para a estratégia.')); return; }
    const num = parseFloat(newTarget.replace(',', '.'));
    if ((needsCpa || needsRoas) && !(num > 0)) {
      toast.error(needsCpa ? t('campaigns.rmf.portfolioCpaRequired', 'Informe o CPA alvo.') : t('campaigns.rmf.portfolioRoasRequired', 'Informe o ROAS alvo.'));
      return;
    }
    let target: number | null = null;
    if (needsCpa && num > 0) target = num;
    if ((needsRoas || optionalRoas) && num > 0) target = num / 100; // % -> razão (400% = 4)
    setCreating(true);
    try {
      const res = await api.post('/campaigns/google/portfolio-strategies', { name, type: newType, target });
      const rn: string | null = res.data?.data?.resource_name || null;
      await load();
      if (rn) onChange(rn, name);
      toast.success(t('campaigns.rmf.portfolioCreated', 'Estratégia de portfólio criada no Google Ads.'));
      setShowCreate(false);
      setNewName('');
      setNewTarget('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('campaigns.rmf.portfolioCreateError', 'Erro ao criar a estratégia de portfólio.'));
    } finally {
      setCreating(false);
    }
  };

  const describeTarget = (s: PortfolioStrategy) => {
    if (s.target_cpa) return `${t('campaigns.rmf.portfolioTypeTargetCpa', 'CPA alvo')}: ${currencySymbol} ${Number(s.target_cpa).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    if (s.target_roas) return `${t('campaigns.rmf.portfolioTypeTargetRoas', 'ROAS alvo')}: ${Math.round(Number(s.target_roas) * 100)}%`;
    return null;
  };

  return (
    <div className="mt-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
          <Share2 className="w-4 h-4" />
          {t('campaigns.rmf.portfolioPick', 'Estratégias de portfólio da conta Google Ads')}
        </div>
        <button type="button" onClick={() => load()} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1" disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {t('campaigns.rmf.reload', 'Recarregar')}
        </button>
      </div>

      {loading && list.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />{t('common.loading', 'Carregando...')}</div>
      )}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {!loading && !error && list.length === 0 && (
        <p className="text-sm text-gray-500">{t('campaigns.rmf.portfolioEmpty', 'Nenhuma estratégia de portfólio na conta. Crie uma abaixo.')}</p>
      )}

      {list.length > 0 && (
        <div className="space-y-2">
          {list.map(s => {
            const isSel = value === s.resource_name;
            const target = describeTarget(s);
            return (
              <button
                key={s.resource_name}
                type="button"
                onClick={() => onChange(s.resource_name, s.name)}
                className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                  isSel ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-white dark:bg-gray-900/30'
                }`}
              >
                <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${isSel ? 'border-primary-500 bg-primary-500' : 'border-gray-300'}`}>
                  {isSel && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</span>
                  <span className="block text-xs text-gray-500">
                    {typeLabel(s.type)}
                    {target ? ` · ${target}` : ''}
                    {typeof s.campaigns === 'number' ? ` · ${t('campaigns.rmf.portfolioCampaigns', '{{count}} campanha(s)', { count: s.campaigns })}` : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!showCreate ? (
        <button type="button" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
          <Plus className="w-4 h-4" />
          {t('campaigns.rmf.portfolioCreateNew', 'Criar nova estratégia de portfólio')}
        </button>
      ) : (
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('campaigns.rmf.portfolioName', 'Nome da estratégia')}</label>
              <input className="input" value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('campaigns.rmf.portfolioNamePh', 'Ex.: CPA alvo - Pesquisa')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('campaigns.rmf.portfolioType', 'Tipo')}</label>
              <select className="input" value={newType} onChange={e => { setNewType(e.target.value as PortfolioType); setNewTarget(''); }}>
                <option value="TARGET_CPA">{typeLabel('TARGET_CPA')}</option>
                <option value="TARGET_ROAS">{typeLabel('TARGET_ROAS')}</option>
                <option value="MAXIMIZE_CONVERSIONS">{typeLabel('MAXIMIZE_CONVERSIONS')}</option>
                <option value="MAXIMIZE_CONVERSION_VALUE">{typeLabel('MAXIMIZE_CONVERSION_VALUE')}</option>
              </select>
            </div>
          </div>
          {newType !== 'MAXIMIZE_CONVERSIONS' && (
            <div className="max-w-xs">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {needsCpa
                  ? t('campaigns.rmf.portfolioTargetCpa', 'CPA alvo ({{cur}})', { cur: currencySymbol })
                  : optionalRoas
                    ? t('campaigns.rmf.portfolioTargetRoasOptional', 'ROAS alvo (%) — opcional')
                    : t('campaigns.rmf.targetRoasPct', 'ROAS alvo (%)')}
              </label>
              <input type="number" className="input" min="0.01" step={needsCpa ? '1' : '10'} value={newTarget} onChange={e => setNewTarget(e.target.value)} placeholder={needsCpa ? '25.00' : '400'} />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={create} disabled={creating} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('campaigns.rmf.portfolioCreate', 'Criar e usar')}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">
              {t('common.cancel', 'Cancelar')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GooglePortfolioStrategyPicker;
